import { ISelectOption } from '@private/components';
import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { ITLLimitForm } from 'components/LimitFormSidebar/types';
import { IParameterPayload } from 'components/ParameterFormSidebar/types';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import {
  IAllPageFilters,
  IBaseGist,
  IGroupEditPayload,
  INullable,
  IPageType,
  TObject,
  TSearch,
} from 'utils/types';

import {
  normalizeCopyTLLimitsResult,
  normalizeLinks,
  normalizeTerminalLink,
  normalizeTerminalLinkHealth,
  normalizeTerminalLinkIntegrationsRequiredParameters,
  normalizeTerminalLinkLimits,
  normalizeTerminalLinkRequiredParameters,
  normalizeTerminalLinksGroups,
  normalizeTerminalLinksLimits,
  normalizeTerminalLinksParameters,
  normalizeTerminalsLinks,
  normalizeTLAction,
  normalizeTLGroup,
  normalizeTLGroupLimits,
  normalizeTLGroupUniqueCustomerIdCounts,
  normalizeTLParameterLowerLevelValues,
  normalizeTLParametersNames,
  normalizeTLSettlementConfigurations,
} from './normalizers';
import {
  ICopyTLLimitsPayload,
  ICopyTLLimitsResult,
  ICreateRequiredParametersForm,
  ICreateTerminalLinkPayload,
  IEditTerminalLinkPayload,
  IGetAllRelatedEntitiesPayload,
  IGetTerminalLinksPayload,
  IGetTLGroupTerminalLinksPayload,
  IGetTLGroupTerminalLinksResult,
  IGetTLParameterLowerLevelValuesPayload,
  IGroupEditRequiredParametersForm,
  IGroupEditTLLimitsPayload,
  IGroupEditTLParametersPayload,
  IRequiredParameters,
  ISoftResetTLParameterPayload,
  ITerminalGroup,
  ITerminalGroupData,
  ITerminalGroupLimits,
  ITerminalLink,
  ITerminalLinkAction,
  ITerminalLinkCopyForm,
  ITerminalLinkHealth,
  ITerminalLinkLimit,
  ITerminalLinksGroups,
  ITerminalLinksLimits,
  ITerminalLinksLimitsData,
  ITerminalLinksParameters,
  ITerminalsLinks,
  ITerminalsLinksData,
  ITLActionPayload,
  ITLGroupForm,
  ITLGroupSettlementConfigurationsForm,
  ITLGroupTerminalLinksPayload,
  ITLGroupUniqueCustomerIdCounts,
  ITLLimitPayload,
  ITLParameterLowerLevelValues,
  ITLParametersNames,
  ITLSettlementConfigurations,
  ITLSettlementConfigurationsForm,
  TTLActionData,
} from './types';

const terminalsLinksApi = {
  async getAllTerminalsLinks(
    filters: IAllPageFilters,
  ): Promise<IPageType<ITerminalsLinks>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('terminal-links', {
      params: normalizedFilter,
    });

    return normalizePageData<ITerminalsLinksData, ITerminalsLinks>(
      response.data,
      normalizeTerminalsLinks,
      JSON.stringify(filters.search),
    );
  },

  async getTerminalLink(id: string): Promise<ITerminalLink> {
    const response = await axiosInstance.get(`terminal-links/${id}`);

    return normalizeTerminalLink(response.data);
  },

  async getTerminalLinkHealth(id: string): Promise<ITerminalLinkHealth> {
    const response = await axiosInstance.get(`terminal-links/${id}/health`);

    return normalizeTerminalLinkHealth(response.data);
  },

  async createTerminalLink(payload: ICreateTerminalLinkPayload): Promise<ITerminalLink> {
    const normalizedTerminalLink = prepareFormData({
      ...payload,
      whitelistIps: payload?.whitelistIps?.join(';'),
    });
    const result = await axiosInstance.post(`terminal-links`, normalizedTerminalLink);

    return normalizeTerminalLink(result.data);
  },

  async editTerminalLink({
    id,
    payload,
  }: IEditTerminalLinkPayload): Promise<AxiosResponse> {
    const normalizedTerminalLink = prepareFormData({
      ...payload,
      whitelistIps: payload?.whitelistIps?.join(';'),
    });
    const result = await axiosInstance.put(
      `terminal-links/${id}`,
      normalizedTerminalLink,
    );
    return result.data;
  },

  async getAllTerminalLinksLimits(
    filters: IAllPageFilters,
  ): Promise<IPageType<ITerminalLinksLimits>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('terminal-links/limits', {
      params: normalizedFilter,
    });

    return normalizePageData<ITerminalLinksLimitsData, ITerminalLinksLimits>(
      response.data,
      normalizeTerminalLinksLimits,
      JSON.stringify(filters.search),
    );
  },

  async groupEditTLLimits({
    filters,
    settings,
  }: IGroupEditTLLimitsPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(
      `terminal-links/limits`,
      normalizedSettings,
      {
        params: { search },
      },
    );

    return response;
  },

  async groupEditGroupLimits({
    filters,
    settings,
  }: IGroupEditTLLimitsPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(
      `terminal-link-groups/limits`,
      normalizedSettings,
      { params: { search } },
    );

    return response;
  },

  async getAllTerminalLinksParameters(
    filters: IAllPageFilters,
  ): Promise<IPageType<ITerminalLinksParameters>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('terminal-links/parameters', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, ITerminalLinksParameters>(
      response.data,
      normalizeTerminalLinksParameters,
      JSON.stringify(filters.search),
    );
  },

  async getTerminalLinksParametersNames(search?: TSearch): Promise<ITLParametersNames[]> {
    const normalizedFilter = normalizeSearchFiltersPayload({ search });

    const response = await axiosInstance.get('terminal-links/parameters/names', {
      params: normalizedFilter,
    });

    return response.data?.result?.map(normalizeTLParametersNames) || [];
  },

  async groupEditTLParameters({
    filters,
    settings,
  }: IGroupEditTLParametersPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.patch(`terminal-links/parameters`, settings, {
      params: { search },
    });

    return response;
  },

  async resetTLParameter(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`terminal-links/parameters/${id}`);

    return response;
  },

  async getTLParameterLowerLevelValues({
    bankParameterId,
    terminalLinkId,
  }: IGetTLParameterLowerLevelValuesPayload): Promise<ITLParameterLowerLevelValues> {
    const response = await axiosInstance.get(
      `terminal-links/${terminalLinkId}/parameters/${bankParameterId}/lower-level-values`,
    );

    return normalizeTLParameterLowerLevelValues(response.data);
  },

  async softResetTLParameter({
    id,
    payload,
  }: ISoftResetTLParameterPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData({ ...payload, terminalLinkId: id });
    const result = await axiosInstance.put(
      `terminal-links/parameters`,
      normalizedPayload,
    );

    return result.data;
  },

  async getAllTerminalLinksGroups(
    filters: IAllPageFilters,
  ): Promise<IPageType<ITerminalLinksGroups>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('terminal-link-groups', {
      params: normalizedFilter,
    });

    return normalizePageData<ITerminalGroupData, ITerminalLinksGroups>(
      response.data,
      normalizeTerminalLinksGroups,
      JSON.stringify(filters.search),
    );
  },

  async getTerminalLinkLimits({
    filters,
    id,
  }: IGetAllRelatedEntitiesPayload): Promise<IPageType<ITerminalLinkLimit>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`terminal-links/${id}/limits`, {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, ITerminalLinkLimit>(
      response.data,
      normalizeTerminalLinkLimits,
      JSON.stringify(filters.search),
    );
  },

  async getTerminalLinksList({
    disabledText,
    enabledText,
    showWithId,
    showWithMerchant,
    showWithStateFlag,
    ...params
  }: IGetTerminalLinksPayload): Promise<IGetTLGroupTerminalLinksResult> {
    const response = await axiosInstance.get(`terminal-links/descriptions`, {
      params,
    });

    return normalizeLinks(response.data, {
      disabledText,
      enabledText,
      showWithId,
      showWithMerchant,
      showWithStateFlag,
    });
  },

  async groupEditTL({ filters, settings }: IGroupEditPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const { whitelistIps, ...result } = settings;

    const response = await axiosInstance.patch(
      `terminal-links`,
      {
        ...result,
        whitelistIps: whitelistIps
          ? (settings?.whitelistIps as (null | string)[])?.join(';') || null
          : undefined,
      },
      { params: { search } },
    );

    return response;
  },

  async getTLGroup(id: string): Promise<ITerminalGroup> {
    const response = await axiosInstance.get(`terminal-link-groups/${id}`);

    return normalizeTLGroup(response.data);
  },

  async getTLGroupUniqueCustomerIdCounts(
    id: string,
  ): Promise<ITLGroupUniqueCustomerIdCounts> {
    const response = await axiosInstance.get(
      `terminal-link-groups/${id}/unique-customer-id-counts`,
    );

    return normalizeTLGroupUniqueCustomerIdCounts(response.data);
  },

  async createTLGroup(payload: ITLGroupForm): Promise<AxiosResponse> {
    const normalizedGroup = prepareFormData(payload);
    const result = await axiosInstance.post(`terminal-link-groups`, normalizedGroup);
    return result.data;
  },

  async editTLGroup(payload: ITLGroupForm): Promise<IBaseGist> {
    const { id, ...parameters } = payload;
    const normalizedPayload = prepareFormData(parameters);

    const response = await axiosInstance.put(
      `terminal-link-groups/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async getAllTLLimits({
    filters,
    id,
  }: IGetAllRelatedEntitiesPayload): Promise<IPageType<ITerminalGroupLimits>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`terminal-link-groups/${id}/limits`, {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, ITerminalGroupLimits>(
      response.data,
      normalizeTLGroupLimits,
      JSON.stringify(filters.search),
    );
  },

  async createParameter({ id, payload }: IParameterPayload): Promise<AxiosResponse> {
    const normalizedTParameter = prepareFormData({ ...payload, terminalLinkId: id });
    const result = await axiosInstance.put(
      `terminal-links/parameters`,
      normalizedTParameter,
    );
    return result.data;
  },

  async createTLLimit({ id, payload }: ITLLimitPayload): Promise<AxiosResponse> {
    const normalizedTLimit = prepareFormData(payload);
    const result = await axiosInstance.post(
      `terminal-links/${id}/limits`,
      normalizedTLimit,
    );
    return result.data;
  },

  async updateLimit({ id: limitId, ...payload }: ITLLimitForm): Promise<AxiosResponse> {
    const normalizedTLimit = prepareFormData(payload);
    const result = await axiosInstance.put(
      `terminal-links/limits/${limitId}`,
      normalizedTLimit,
    );
    return result.data;
  },

  async createGroupLimit({ id, payload }: ITLLimitPayload): Promise<AxiosResponse> {
    const normalizedTLimit = prepareFormData(payload);
    const result = await axiosInstance.post(
      `terminal-link-groups/${id}/limits`,
      normalizedTLimit,
    );
    return result.data;
  },

  async updateGroupLimit({
    id: limitId,
    ...payload
  }: ITLLimitForm): Promise<AxiosResponse> {
    const normalizedTLimit = prepareFormData(payload);
    const result = await axiosInstance.put(
      `terminal-link-groups/limits/${limitId}`,
      normalizedTLimit,
    );
    return result.data;
  },

  async addTLGroupTerminalLinks({
    id,
    terminalLinks,
  }: ITLGroupTerminalLinksPayload): Promise<IBaseGist> {
    const normalizedLinks = prepareFormData({ value: terminalLinks });
    const result = await axiosInstance.put(
      `terminal-link-groups/${id}/terminal-links`,
      normalizedLinks,
    );
    return result.data;
  },

  async deleteTerminalLinkLimit(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`terminal-links/limits/${id}`);

    return response.data;
  },

  async deleteTerminalLinkGroupLimit(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`terminal-link-groups/limits/${id}`);

    return response.data;
  },

  async deleteTerminalLink(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`terminal-links/${id}`);

    return result;
  },

  async deleteTerminalLinkGroup(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`terminal-link-groups/${id}`);

    return result;
  },

  async getTLGroupTerminalLinkList({
    showWithId,
    ...params
  }: IGetTLGroupTerminalLinksPayload): Promise<ISelectOption[]> {
    const response = await axiosInstance.get(
      `terminal-link-groups/terminal-links/descriptions`,
      {
        params,
      },
    );

    return normalizeLinks(response.data, {
      showWithId,
      showWithMerchant: true,
    }).terminalLinks;
  },

  async copyTerminalLinks(payload: ITerminalLinkCopyForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(`terminal-links/copy`, normalizedPayload);
    return result;
  },

  async getTerminalLinkRequiredParameters(id: string): Promise<IRequiredParameters> {
    const response = await axiosInstance.get(`terminal-links/${id}/required-fields`);

    return normalizeTerminalLinkRequiredParameters(response.data);
  },

  async changeRequiredParameters({
    id,
    transactionType,
    ...payload
  }: ICreateRequiredParametersForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `terminal-links/${id}/required-fields/${transactionType}`,
      normalizedPayload,
    );

    return response;
  },

  async groupEditRequiredParameters(
    payload: IGroupEditRequiredParametersForm,
  ): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.patch(
      `terminal-links/required-fields`,
      normalizedPayload,
    );

    return response;
  },

  async getSettlementConfigs(id: string): Promise<ITLSettlementConfigurations> {
    const { data } = await axiosInstance.get(`terminal-links/${id}/settlements`);
    return normalizeTLSettlementConfigurations(data);
  },

  async updateSettlementConfigs(
    id: string,
    payload: ITLSettlementConfigurationsForm,
  ): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `terminal-links/${id}/settlements`,
      normalizedPayload,
    );

    return response;
  },

  async groupCreateSettlementConfigs({
    search,
    ...props
  }: ITLGroupSettlementConfigurationsForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(props);
    const normalizedFilter = normalizeSearchFiltersPayload(search);

    const response = await axiosInstance.post(
      `terminal-links/settlements`,
      normalizedPayload,
      { params: { search: normalizedFilter.search } },
    );

    return response;
  },

  async getAllTerminalsLinkActions(
    filters: IAllPageFilters,
  ): Promise<IPageType<ITerminalLinkAction>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('terminal-links/actions', {
      params: normalizedFilter,
    });

    return normalizePageData<TTLActionData, ITerminalLinkAction>(
      response.data,
      normalizeTLAction,
      JSON.stringify(filters.search),
    );
  },

  async getTerminalLinkAction(id: number): Promise<ITerminalLinkAction> {
    const response = await axiosInstance.get(`terminal-links/actions/${id}`);
    return normalizeTLAction(response.data);
  },

  async createTerminalLinkAction({ payload }: ITLActionPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `terminal-links/actions`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editTerminalLinkAction({ id, payload }: ITLActionPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `terminal-links/actions/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteTerminalsLinkAction(id: INullable<number>): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`terminal-links/actions/${id}`);

    return result;
  },

  async copyLimits(payload: ICopyTLLimitsPayload): Promise<ICopyTLLimitsResult> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.post(
      'terminal-links/limits/copy',
      normalizedPayload,
    );

    return normalizeCopyTLLimitsResult(response?.data);
  },

  async getTerminalLinkIntegrationsRequiredParameters({
    id,
    type,
  }: {
    id: string;
    type: string;
  }): Promise<IRequiredParameters> {
    const response = await axiosInstance.post(
      `terminal-links/${id}/required-fields/simulation/${type}`,
    );

    return normalizeTerminalLinkIntegrationsRequiredParameters(response.data);
  },
};

export default terminalsLinksApi;
